import { ReadonlyVec2, ReadonlyVec3 } from 'gl-matrix';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { PlaneView } from '~/engine/view/views/PlaneView';

/** The scene object a {@link BrushCursor} shows and moves. */
export interface BrushCursorShape extends CustomTargetObject {
    setPosition(position: ReadonlyVec3): void;
    /** @param diameter Brush diameter in millimeters */
    resize(diameter: number): void;
}

/**
 * Creates the cursor shape on `view`.
 * @param coordinates Client pixel coordinates of the mouse
 * @param diameter Brush diameter in millimeters
 */
export type BrushCursorShapeFactory = (
    view: PlaneView,
    coordinates: ReadonlyVec2,
    diameter: number
) => BrushCursorShape;

/**
 * The brush outline under the mouse: a {@link BrushCursorShape} shown on
 * the one view the mouse is over. The shape is created when the cursor
 * first moves onto a view, moved while it stays there, rebuilt in place on
 * {@link refresh}, and removed when it moves to another view or is hidden.
 */
export class BrushCursor {
    private shown:
        | {
              view: PlaneView;
              shape: BrushCursorShape;
              /** Client pixel coordinates the shape was last put at. */
              coordinates: ReadonlyVec2;
          }
        | undefined;

    /** @param diameter Brush diameter in millimeters */
    public constructor(
        private readonly createShape: BrushCursorShapeFactory,
        private diameter: number
    ) {}

    /**
     * Show the cursor at the mouse position on `view`.
     * @param coordinates Client pixel coordinates of the mouse
     */
    public moveTo(view: PlaneView, coordinates: ReadonlyVec2): void {
        if (this.shown && this.shown.view !== view) {
            this.hide();
        }
        if (this.shown) {
            this.shown.shape.setPosition(
                view.clientToPatientCoordinates(coordinates)
            );
            this.shown.coordinates = coordinates;
        } else {
            const shape = this.createShape(view, coordinates, this.diameter);
            view.addCustomObject(shape);
            this.shown = { view, shape, coordinates };
        }
        view.renderLayers();
    }

    /** The view the cursor is shown on, if any. */
    public get shownOn(): PlaneView | undefined {
        return this.shown?.view;
    }

    /**
     * Rebuild the shape where it is shown, so it reflects what the factory
     * now creates. Nothing happens while the cursor is hidden.
     */
    public refresh(): void {
        if (!this.shown) {
            return;
        }
        const { view, coordinates } = this.shown;
        this.hide();
        this.moveTo(view, coordinates);
    }

    /** @param diameter Brush diameter in millimeters */
    public resize(diameter: number): void {
        this.diameter = diameter;
        if (this.shown) {
            this.shown.shape.resize(diameter);
            this.shown.view.renderLayers();
        }
    }

    /** Remove the cursor from the view it is shown on, if any. */
    public hide(): void {
        if (!this.shown) {
            return;
        }
        const { view, shape } = this.shown;
        this.shown = undefined;
        view.removeCustomObject(shape);
        view.renderLayers();
    }
}
